import type {
  CorpseState,
  NetworkCallbacks,
  PlayerState,
  ServerMessage,
  ShotState,
  WelcomeMessage,
} from './types';

function getWebSocketUrl(): string {
  const configured = import.meta.env.VITE_WS_URL as string | undefined;
  if (configured) return configured;

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return `${protocol}//${window.location.hostname}:8787/ws`;
  }
  return `${protocol}//${window.location.host}/ws`;
}

export class NetworkClient {
  private socket?: WebSocket;
  private readonly name: string;
  private readonly callbacks: NetworkCallbacks;
  private reconnectTimer?: number;
  private retries = 0;
  private closedByUser = false;

  connected = false;
  id = `offline-${crypto.randomUUID()}`;

  constructor(name: string, callbacks: NetworkCallbacks) {
    this.name = name;
    this.callbacks = callbacks;
  }

  connect(): void {
    this.closedByUser = false;
    try {
      this.socket = new WebSocket(getWebSocketUrl());
    } catch {
      this.callbacks.onConnected(false);
      return;
    }

    this.socket.addEventListener('open', () => {
      this.connected = true;
      this.retries = 0;
      this.callbacks.onConnected(true);
      this.send({ type: 'join', name: this.name });
    });

    this.socket.addEventListener('message', (event) => {
      try {
        this.handle(JSON.parse(String(event.data)) as ServerMessage);
      } catch {
        // A malformed packet must never stop the offline-capable game loop.
      }
    });

    this.socket.addEventListener('close', () => {
      this.connected = false;
      this.callbacks.onConnected(false);
      if (!this.closedByUser && this.retries < 2) {
        this.retries += 1;
        this.reconnectTimer = window.setTimeout(() => this.connect(), 1200 * this.retries);
      }
    });

    this.socket.addEventListener('error', () => {
      this.callbacks.onConnected(false);
    });
  }

  private handle(message: ServerMessage): void {
    switch (message.type) {
      case 'welcome':
        this.id = message.id;
        this.callbacks.onWelcome(message as WelcomeMessage);
        break;
      case 'player:join':
      case 'player:state':
        if (message.player.id !== this.id) this.callbacks.onPlayerState(message.player);
        break;
      case 'player:leave':
        this.callbacks.onPlayerLeave(message.id);
        break;
      case 'corpse:add':
        this.callbacks.onCorpseAdd(message.corpse);
        break;
      case 'corpse:move':
        this.callbacks.onCorpseMove(message.corpse);
        break;
      case 'corpse:remove':
        this.callbacks.onCorpseRemove(message.id, message.reason);
        break;
      case 'combat:shot':
        if (message.shot.ownerId !== this.id) this.callbacks.onShot(message.shot);
        break;
      case 'world:count':
        this.callbacks.onWorldCount(message.players, message.corpses);
        break;
      case 'error':
        break;
    }
  }

  sendPlayerState(player: Omit<PlayerState, 'id' | 'name'>): void {
    this.send({ type: 'player:state', player: { ...player, id: this.id, name: this.name } });
  }

  addCorpse(corpse: CorpseState): void {
    this.send({ type: 'corpse:add', corpse });
  }

  moveCorpse(corpse: CorpseState): void {
    this.send({ type: 'corpse:move', corpse });
  }

  damageWall(id: string, damage: number): void {
    this.send({ type: 'wall:damage', id, damage });
  }

  sendShot(shot: ShotState): void {
    this.send({ type: 'combat:shot', shot });
  }

  private send(payload: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
    }
  }

  close(): void {
    this.closedByUser = true;
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.socket?.close();
  }
}
